import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import api, { fileUrl } from "@/lib/api";
import { toEmbed, isVideoFile } from "@/lib/richtext";
import { toast } from "sonner";
import {
  TextB, TextItalic, TextHTwo, TextHThree, ListBullets, ListNumbers, Quotes, LinkSimple,
  Image as ImageIcon, VideoCamera, MicrophoneStage, TextAlignLeft, TextAlignCenter, TextAlignRight, ArrowUUpLeft, ArrowUUpRight,
} from "@phosphor-icons/react";

// Image with an alignment attribute (left/right float = text wrap, center = block).
const RtImage = Image.configure({ inline: false, allowBase64: true }).extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: "center",
        parseHTML: (el) => el.getAttribute("data-align") || "center",
        renderHTML: (attrs) => ({ "data-align": attrs.align, class: `rt-img rt-${attrs.align}` }),
      },
    };
  },
});

// Embedded iframe (YouTube / Vimeo / Spotify / Apple podcasts / generic).
const Iframe = Node.create({
  name: "iframe", group: "block", atom: true, selectable: true,
  addAttributes() { return { src: { default: null } }; },
  parseHTML() { return [{ tag: "iframe" }]; },
  renderHTML({ HTMLAttributes }) {
    return ["div", { class: "rt-embed" }, ["iframe", mergeAttributes(HTMLAttributes, {
      allowfullscreen: "true", frameborder: "0", loading: "lazy",
      allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
    })]];
  },
});

// Self-hosted video file.
const VideoNode = Node.create({
  name: "videofile", group: "block", atom: true, selectable: true,
  addAttributes() { return { src: { default: null } }; },
  parseHTML() { return [{ tag: "video" }]; },
  renderHTML({ HTMLAttributes }) {
    return ["div", { class: "rt-embed" }, ["video", mergeAttributes(HTMLAttributes, { controls: "true", class: "rt-video" })]];
  },
});

function Btn({ onClick, active, title, children, testid }) {
  return (
    <button type="button" title={title} data-testid={testid} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className={`grid h-8 w-8 place-items-center rounded-md transition-colors ${active ? "bg-secondary/15 text-secondary" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder = "Write here…", testid = "rte" }) {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      RtImage,
      Iframe,
      VideoNode,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    editorProps: { attributes: { class: "rich-content prose max-w-none min-h-[240px] focus:outline-none", "data-placeholder": placeholder } },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Reflect external value changes (e.g. AI draft / append details) without clobbering typing.
  useEffect(() => {
    if (editor && (value || "") !== editor.getHTML()) editor.commands.setContent(value || "", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;
  const align = (dir) => {
    if (editor.isActive("image")) editor.chain().focus().updateAttributes("image", { align: dir }).run();
    else editor.chain().focus().setTextAlign(dir).run();
  };
  const uploadImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      editor.chain().focus().setImage({ src: fileUrl(data.id || data.file_id), align: "center" }).run();
    } catch { toast.error("Image upload failed"); }
    e.target.value = "";
  };
  const imageByUrl = () => { const u = window.prompt("Image URL"); if (u) editor.chain().focus().setImage({ src: u.trim(), align: "center" }).run(); };
  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const u = window.prompt("Link URL (empty to remove)", prev || "https://");
    if (u === null) return;
    if (!u.trim()) return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: u.trim() }).run();
  };
  const embedMedia = (label) => {
    const u = window.prompt(`${label} URL (YouTube, Vimeo, Spotify, Apple Podcasts, or an .mp4 link)`);
    if (!u) return;
    const url = u.trim();
    if (isVideoFile(url)) editor.chain().focus().insertContent({ type: "videofile", attrs: { src: url } }).run();
    else editor.chain().focus().insertContent({ type: "iframe", attrs: { src: toEmbed(url) } }).run();
  };

  return (
    <div className="overflow-hidden rounded-xl border border-input bg-background" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border/60 bg-muted/30 p-1.5">
        <Btn testid={`${testid}-bold`} title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><TextB size={16} weight="bold" /></Btn>
        <Btn testid={`${testid}-italic`} title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><TextItalic size={16} /></Btn>
        <Btn testid={`${testid}-h2`} title="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><TextHTwo size={16} /></Btn>
        <Btn testid={`${testid}-h3`} title="Subheading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><TextHThree size={16} /></Btn>
        <Btn testid={`${testid}-ul`} title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><ListBullets size={16} /></Btn>
        <Btn testid={`${testid}-ol`} title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListNumbers size={16} /></Btn>
        <Btn testid={`${testid}-quote`} title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quotes size={16} /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn testid={`${testid}-align-left`} title="Align left / wrap text right of image" onClick={() => align("left")}><TextAlignLeft size={16} /></Btn>
        <Btn testid={`${testid}-align-center`} title="Center" onClick={() => align("center")}><TextAlignCenter size={16} /></Btn>
        <Btn testid={`${testid}-align-right`} title="Align right / wrap text left of image" onClick={() => align("right")}><TextAlignRight size={16} /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn testid={`${testid}-image`} title="Upload image" onClick={() => fileRef.current?.click()}><ImageIcon size={16} /></Btn>
        <Btn testid={`${testid}-image-url`} title="Image by URL" onClick={imageByUrl}><ImageIcon size={16} weight="duotone" /></Btn>
        <Btn testid={`${testid}-video`} title="Embed video (YouTube/Vimeo/mp4)" onClick={() => embedMedia("Video")}><VideoCamera size={16} /></Btn>
        <Btn testid={`${testid}-podcast`} title="Embed podcast (Spotify/Apple)" onClick={() => embedMedia("Podcast")}><MicrophoneStage size={16} /></Btn>
        <Btn testid={`${testid}-link`} title="Link" active={editor.isActive("link")} onClick={setLink}><LinkSimple size={16} /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn testid={`${testid}-undo`} title="Undo" onClick={() => editor.chain().focus().undo().run()}><ArrowUUpLeft size={16} /></Btn>
        <Btn testid={`${testid}-redo`} title="Redo" onClick={() => editor.chain().focus().redo().run()}><ArrowUUpRight size={16} /></Btn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid={`${testid}-file`} />
      </div>
      <EditorContent editor={editor} className="max-h-[520px] overflow-y-auto px-4 py-3" data-testid={`${testid}-area`} />
    </div>
  );
}
